import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import { categories, articles } from '@/mocks/categories';

export default function Home() {
  const [activeFaq, setActiveFaq] = useState<number | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setVisible(true);
  }, []);

  const latestArticles = articles.slice(0, 6);
  const faqArticles = articles.filter((a) => a.categoryId === 'faq').slice(0, 5);

  const webSiteJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'KAIYUN导航 - kaiyun.con 官方入口导航',
    url: 'https://kaiyun-navi.com',
    description:
      '为用户提供关于 kaiyun.con 的完整导航指引、常见问题解答及访问说明，帮助用户快速找到所需内容，了解相关平台功能与服务详情。',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://kaiyun-navi.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const webPageJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'KAIYUN导航 - kaiyun.con 官方入口导航 | 首页',
    description:
      'www.kaiyun.con 是用户高频搜索的入口关键词，本页为您提供完整导航指引、常见问题解答及访问说明。',
    url: 'https://kaiyun-navi.com',
    isPartOf: {
      '@type': 'WebSite',
      name: 'KAIYUN导航',
      url: 'https://kaiyun-navi.com',
    },
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqArticles.map((a) => ({
      '@type': 'Question',
      name: a.title,
      acceptedAnswer: {
        '@type': 'Answer',
        text: a.summary,
      },
    })),
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webSiteJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
      />

      <SEOHead
        title="www.kaiyun.con 官方入口导航 | kaiyun.con入口 - KAIYUN导航"
        description="www.kaiyun.con 是用户高频搜索的入口关键词，本页为您提供关于 www.kaiyun.con 的完整导航指引、常见问题解答及访问说明，帮助用户快速找到所需内容，了解相关平台功能与服务详情。"
        keywords="www.kaiyun.con,kaiyun.con入口,www.kaiyun.con官网,kaiyun.con访问指南,kaiyun.con常见问题,kaiyun.con网址导航,kaiyun.con平台说明"
        canonicalUrl="https://kaiyun-navi.com"
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          {/* Hero Section */}
          <section className="relative w-full h-[420px] md:h-[600px] flex items-center justify-center overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Modern%20abstract%20geometric%20composition%20with%20warm%20amber%20and%20dark%20charcoal%20tones%2C%20flowing%20lines%20and%20dynamic%20movement%2C%20clean%20minimalist%20style%2C%20professional%20atmosphere%2C%20soft%20lighting%2C%20elegant%20gradient%20background%2C%20no%20text&width=1600&height=900&seq=hero-bg-01&orientation=landscape"
              alt="kaiyun.con 官方入口导航"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>
            <div className="relative z-10 text-center px-4 w-full max-w-4xl mx-auto">
              <div
                className={`transition-all duration-700 ${
                  visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
              >
                <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold text-white mb-4 font-[var(--font-heading)]">
                  <strong>www.kaiyun.con</strong> 官方入口导航
                </h1>
                <p className="text-base md:text-lg text-white/80 max-w-2xl mx-auto mb-8 leading-relaxed">
                  为您提供 <strong>kaiyun.con</strong> 完整导航指引、访问说明与常见问题解答，帮助您快速找到所需内容
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <Link
                    to="/categories"
                    className="w-full sm:w-auto px-8 py-3 bg-primary-500 text-background-50 rounded-lg text-base font-medium hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap no-underline"
                  >
                    查看分类导航
                    <i className="ri-arrow-right-line ml-2"></i>
                  </Link>
                  <Link
                    to="/about"
                    className="w-full sm:w-auto px-8 py-3 bg-background-50/20 text-white border border-white/30 rounded-lg text-base font-medium hover:bg-background-50/30 transition-all cursor-pointer whitespace-nowrap no-underline backdrop-blur-sm"
                  >
                    了解平台详情
                  </Link>
                </div>
              </div>
            </div>
          </section>

          {/* Quick Nav Section */}
          <section className="py-10 md:py-14 bg-background-50">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="text-center mb-8 md:mb-10">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-[var(--font-heading)]">
                  快速导航
                </h2>
                <p className="text-sm md:text-base text-foreground-600 mt-2">
                  选择您感兴趣的内容分类，快速找到所需信息
                </p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                {categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/categories?tab=${cat.id}`}
                    className="group p-5 bg-background-50 border border-background-200 rounded-lg text-center hover:border-primary-300 hover:bg-primary-50/30 transition-all cursor-pointer no-underline"
                  >
                    <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center bg-primary-100 rounded-lg group-hover:bg-primary-500 transition-colors">
                      <i
                        className={`${cat.icon} text-xl text-primary-500 group-hover:text-background-50 transition-colors`}
                      ></i>
                    </div>
                    <h3 className="text-sm font-medium text-foreground-800 group-hover:text-primary-600 transition-colors">
                      {cat.name}
                    </h3>
                    <p className="text-xs text-foreground-500 mt-1 line-clamp-2">
                      {cat.description}
                    </p>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* About Platform Section */}
          <section className="py-10 md:py-14 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-12">
                <div className="flex-1">
                  <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-[var(--font-heading)] mb-4">
                    关于 <strong>kaiyun.con</strong>
                  </h2>
                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                    <strong>kaiyun.con</strong> 是广受用户关注的综合性体育娱乐在线平台，提供丰富的体育赛事直播、专业数据分析和互动娱乐服务。平台以稳定的技术架构、丰富的赛事资源和优质的用户体验著称，致力于为广大体育爱好者打造一站式的数字娱乐空间。
                  </p>
                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-6">
                    无论您是资深体育迷还是刚接触在线体育平台的新用户，<strong>kaiyun.con</strong> 都能为您提供流畅便捷的服务体验。本导航网站为您整理了最全面的访问指南和使用教程，帮助您轻松上手。
                  </p>
                  <Link
                    to="/about"
                    className="inline-flex items-center gap-2 text-primary-500 hover:text-primary-600 font-medium text-sm transition-colors no-underline"
                  >
                    了解更多关于我们
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
                <div className="flex-1 w-full">
                  <div className="w-full h-64 md:h-80 rounded-lg overflow-hidden">
                    <img
                      src="https://readdy.ai/api/search-image?query=Warm%20modern%20digital%20entertainment%20atmosphere%20with%20abstract%20sports%20elements%2C%20flowing%20light%20trails%20and%20dynamic%20geometric%20shapes%2C%20amber%20and%20cream%20color%20palette%2C%20minimalist%20and%20elegant%20design%2C%20professional%20tech%20environment%2C%20soft%20ambient%20glow%2C%20no%20text%20or%20logos&width=800&height=600&seq=about-platform-01&orientation=landscape"
                      alt="kaiyun.con 平台介绍"
                      className="w-full h-full object-cover rounded-lg"
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Latest Articles */}
          <section className="py-10 md:py-14 bg-background-50">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex items-center justify-between mb-8 md:mb-10">
                <div>
                  <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-[var(--font-heading)]">
                    最新文章
                  </h2>
                  <p className="text-sm text-foreground-600 mt-1">
                    探索最新的访问指南与平台资讯
                  </p>
                </div>
                <Link
                  to="/categories"
                  className="text-sm text-primary-500 hover:text-primary-600 font-medium transition-colors whitespace-nowrap no-underline"
                >
                  查看全部
                  <i className="ri-arrow-right-line ml-1"></i>
                </Link>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {latestArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/articles/${article.id}`}
                    className="group p-5 bg-background-50 border border-background-200 rounded-lg hover:border-primary-200 transition-all cursor-pointer no-underline"
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <span className="px-2 py-0.5 bg-accent-100 text-accent-700 text-xs rounded-full font-medium">
                        {categories.find((c) => c.id === article.categoryId)?.name}
                      </span>
                      <span className="text-xs text-foreground-400">{article.readTime}</span>
                    </div>
                    <h3 className="text-sm md:text-base font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors mb-2 line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="text-xs md:text-sm text-foreground-500 line-clamp-2 mb-3">
                      {article.summary}
                    </p>
                    <div className="flex items-center gap-2 text-xs text-foreground-400">
                      <i className="ri-calendar-line"></i>
                      <span>{article.publishDate}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="py-10 md:py-14 bg-background-100">
            <div className="max-w-3xl mx-auto px-4 md:px-6">
              <div className="text-center mb-8 md:mb-10">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-[var(--font-heading)]">
                  <strong>kaiyun.con</strong> 常见问题
                </h2>
                <p className="text-sm text-foreground-600 mt-2">
                  关于 kaiyun.con 的常见疑问，这里都有答案
                </p>
              </div>
              <div className="space-y-3">
                {faqArticles.map((faq, index) => (
                  <div
                    key={faq.id}
                    className="bg-background-50 border border-background-200 rounded-lg overflow-hidden"
                  >
                    <button
                      onClick={() => setActiveFaq(activeFaq === index ? null : index)}
                      className="w-full flex items-center justify-between p-4 text-left cursor-pointer hover:bg-background-100 transition-colors"
                    >
                      <span className="text-sm md:text-base font-medium text-foreground-800 pr-4">
                        {faq.title}
                      </span>
                      <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                        <i
                          className={`ri-${
                            activeFaq === index ? 'subtract' : 'add'
                          }-line text-lg text-foreground-500 transition-transform`}
                        ></i>
                      </div>
                    </button>
                    {activeFaq === index && (
                      <div className="px-4 pb-4">
                        <p className="text-sm text-foreground-600 leading-relaxed">
                          {faq.summary}
                        </p>
                        <Link
                          to={`/articles/${faq.id}`}
                          className="inline-flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 mt-2 transition-colors no-underline"
                        >
                          查看完整解答
                          <i className="ri-arrow-right-s-line"></i>
                        </Link>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="text-center mt-8">
                <Link
                  to="/categories?tab=faq"
                  className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap no-underline"
                >
                  查看更多常见问题
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-10 md:py-14 bg-background-50">
            <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-[var(--font-heading)] mb-3">
                还没有找到您需要的信息？
              </h2>
              <p className="text-sm md:text-base text-foreground-600 mb-6">
                浏览完整的分类导航页面，获取更详细的 kaiyun.con 相关指引与教程
              </p>
              <Link
                to="/categories"
                className="inline-flex items-center gap-2 px-8 py-3 bg-primary-500 text-background-50 rounded-lg text-base font-medium hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap no-underline"
              >
                进入分类导航
                <i className="ri-compass-3-line"></i>
              </Link>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}